import { DestroyRef, Inject, Injectable, Optional, effect, inject, signal } from '@angular/core';
import { Observable, catchError, filter, map, of } from 'rxjs';
import { PingResponse } from './model/form-service.model';
import { OfflineApplicationDbProviderService } from './rxdb-providers/offline-application-db-provider.service';
import { API_BASE_URL } from './service-shared';
import { environment } from "environments/environment";
import { SwMessenger } from '../service-workers/sw-messenger.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FormServiceProxy } from './form-service.proxy';

@Injectable({
    providedIn: 'root'
})
export class GlobalService {
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _swMessenger: SwMessenger = inject(SwMessenger);
    private readonly _formServiceProxy = inject(FormServiceProxy);
    private readonly _fuseMediaWatcherService = inject(FuseMediaWatcherService);

    private _userId: string = '';
    private baseUrl: string = '';
    private serviceWorkerInitialised = signal<boolean>(false);

    unloading = signal(false);
    isOnline = signal(true);
    isOffline = signal(false);
    modal = signal<ModalOverlayData | null>(null);
    isSynchronised = signal<boolean>(false);
    isMobileView = signal<boolean>(false);

    getCurrentUserId() {
        return this._userId || '';
    }

    setCurrentUserId(userId: string | undefined) {
        this._userId = OfflineApplicationDbProviderService.setCurrentUserId(userId || '');
    }

    constructor(@Optional() @Inject(API_BASE_URL) apiBaseUrl: string) {
        this.baseUrl = (apiBaseUrl ?? '') + (environment.config?.formServiceEndpoint ?? '');
        this.offlineOnlineListeners();
        let wasOnline = false;

        effect(() => {
            let online = this.isOnline();
            if (this.serviceWorkerInitialised()) {
                if (online && !wasOnline) {
                    setTimeout(() => {
                        if (this.isOnline()) {
                            // if still online 2 seconds later...
                            this._formServiceProxy.syncOffline().subscribe();
                        }
                    }, 2000);
                }

                wasOnline = online;
            }
        });

        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(({ matchingAliases }) => {
                // subscribe to event.matchingAliases (of { matchingAliases: string[], matchingQueries: any })
                this.isMobileView.set(!matchingAliases.includes('md')); // 'sm' is mobile... 'md' is mobile tablet
            });

        if ('serviceWorker' in navigator) {
            (<Navigator>navigator).serviceWorker.ready.then((registration: ServiceWorkerRegistration) => {
                this.serviceWorkerInitialised.set(true);
            });
        }
    }

    // Check online status
    verifyConnectivity(): Observable<boolean> {
        // Handle ERR_INTERNET_DISCONNECTED state.
        if (!navigator.onLine) {
            this.setOnline(false);
            return of(false);
        }

        // Check connection to the main service.
        return this._formServiceProxy.ping().pipe(
            map((response: PingResponse) => {
                let online = response.ack > 0;
                this.setOnline(navigator.onLine && online);
                return navigator.onLine && online;
            }),
            catchError(() => {
                this.setOnline(false);
                return of(false);
            })
        );
    }

    setOnline(onlineFlag: boolean) {
        if (this.isOnline() !== onlineFlag) {
            this._swMessenger.postMessage({
                isOnline: onlineFlag,
                baseUrl: this.baseUrl
            });
        }

        this.isOnline.set(onlineFlag);
        this.isOffline.set(!onlineFlag);
    }

    showModal(key: string, text: string, timeout: number = 0) {
        this.modal.set({ key: key, text: text });
        if (timeout > 0) {
            setTimeout(() => {
                this.hideModal(key);
            }, timeout);
        }
    }

    hideModal(key: string) {
        const currentData = this.modal();
        if (currentData?.key !== key) {
            return;
        }

        this.modal.set(null);
    }

    private offlineOnlineListeners() {
        addEventListener("online", (event) => {
            this.verifyConnectivity();
        });

        addEventListener("offline", (event) => {
            this.setOnline(false);
        });
    }
}

export type ModalOverlayData = {
    key: string;
    text: string;
}
